import '../index.scss';

import { LuiIcon, LuiMiniSpinner } from '@linzjs/lui';
import { IconName } from '@linzjs/lui/dist/components/LuiIcon/LuiIcon';
import clsx from 'clsx';
import { forwardRef, ReactElement, useContext } from 'react';
import Skeleton from 'react-loading-skeleton';

import { tabToNextSiblingElement, tabToPreviousSiblingElement } from '../common';
import { RibbonButtonSliderContext } from './RibbonButtonSliderContext';

export interface RibbonButtonProps {
  id?: string;
  popoverTarget?: string;
  popoverTargetAction?: 'toggle' | 'show' | 'hide' | undefined;
  anchorName?: string;
  title?: string;
  icon?: IconName;
  content?: ReactElement;
  disabled?: boolean;
  selected?: boolean;
  loading?: boolean;
  processing?: boolean;
  processingMessage?: string;
  className?: string;
  testId?: string;
  onClick?: () => void;
}

export const RibbonButton = forwardRef<HTMLButtonElement, RibbonButtonProps>(function RibbonButton(
  {
    id,
    popoverTarget,
    popoverTargetAction,
    anchorName,
    title,
    icon,
    content,
    className,
    selected,
    loading,
    processing,
    processingMessage = `Loading ${title}`,
    disabled,
    testId,
    onClick,
  },
  ref,
) {
  const { menuUniqueId, autoClose } = useContext(RibbonButtonSliderContext);

  if (loading) {
    return (
      <div className={'RibbonButtonSkeleton'}>
        <Skeleton
          className={clsx(
            disabled && 'disabled', // disabled to style cursor
          )}
          height={36}
          width={36}
        />
      </div>
    );
  }

  if (processing) {
    return (
      <div
        className={'RibbonButtonProcessing'}
        role={'status'}
        title={processingMessage}
        aria-label={processingMessage}
      >
        <LuiMiniSpinner size={20} />
      </div>
    );
  }

  return (
    <button
      ref={ref}
      id={id}
      style={{ anchorName }}
      {...((popoverTarget || menuUniqueId) && { ['popovertarget' as never]: popoverTarget ?? menuUniqueId })}
      {...((popoverTargetAction || autoClose != null) && {
        ['popovertargetaction' as never]: popoverTargetAction ?? (autoClose ? 'toggle' : 'show'),
      })}
      type="button"
      title={title}
      data-testid={testId}
      disabled={disabled}
      onClick={onClick}
      className={clsx(
        className,
        'RibbonButton',
        selected && 'RibbonButton-selected',
        disabled && 'RibbonButton-disabled',
      )}
      onKeyDown={({ code }) => {
        if (code === 'ArrowDown' || code === 'ArrowRight') {
          tabToNextSiblingElement();
        }
        if (code === 'ArrowUp' || code === 'ArrowLeft') {
          tabToPreviousSiblingElement();
        }
      }}
    >
      {icon && <LuiIcon name={icon} alt={title ?? ''} size={'md'} />}
      {content}
    </button>
  );
});
